import React, { useEffect, useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { reApi, errMsg, fmtDateTime } from "@/lib/residentApi";
import { labelCls } from "./ui";

const HIDE_AFTER = 30;

/** Super admin only: confirm → reveal the full SSN for 30 seconds. Every reveal is logged server-side. */
export function SsnRow({ resident, sa }) {
  const last4 = resident.government_ids?.ssn_last4;
  const [ask, setAsk] = useState(false);
  const [full, setFull] = useState(null);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [views, setViews] = useState(null);
  const loadViews = () => sa && reApi.get(`/residents/${resident.id}/ssn/views`).then(setViews).catch(() => {});
  useEffect(() => { loadViews(); setFull(null); }, [resident.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!full) return undefined;
    if (left <= 0) { setFull(null); return undefined; }
    const t = setTimeout(() => setLeft((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [full, left]);
  const reveal = async () => {
    setBusy(true); setErr(null);
    try { const d = await reApi.post(`/residents/${resident.id}/ssn/reveal`); setFull(d.ssn); setLeft(HIDE_AFTER); loadViews(); }
    catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); setAsk(false); }
  };
  return (
    <div className="p-3 border border-white/5 rounded-sm bg-white/[0.02]">
      <div className={labelCls}>SSN</div>
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid="profile-ssn" className="text-nex-silver text-sm font-mono">{full || (last4 ? `•••-••-${last4}` : "—")}</span>
        {sa && last4 && (full ? (
          <button type="button" onClick={() => setFull(null)} data-testid="ssn-hide" className="inline-flex items-center gap-1 text-[11px] text-nex-muted hover:text-white">
            <EyeOff className="h-3 w-3" /> Hide · auto in <span data-testid="ssn-countdown">{left}</span>s</button>
        ) : (
          <button type="button" onClick={() => setAsk(true)} disabled={busy} data-testid="ssn-show-full" className="inline-flex items-center gap-1 text-[11px] text-nex-gold hover:underline disabled:opacity-50">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Show full SSN</button>
        ))}
      </div>
      {err && <div data-testid="ssn-error" className="text-[11px] text-red-300 mt-1">{err}</div>}
      {sa && views?.total > 0 && (
        <div data-testid="ssn-views" className="mt-2 text-[10px] text-nex-muted space-y-0.5">
          <div>Full SSN viewed {views.total}×{views.total > 3 ? " · latest 3 below, all in the Audit log" : ""}</div>
          {views.views.slice(0, 3).map((v, i) => <div key={i} data-testid={`ssn-view-${i}`}>{v.actor?.name || v.actor?.email} · {fmtDateTime(v.at)}</div>)}
        </div>
      )}
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="ssn-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Show full SSN?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? The full SSN for {resident.first_name} {resident.last_name} will show for 30 seconds. Your name and the time are recorded in the audit log.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ssn-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="ssn-confirm-yes" onClick={reveal}>Yes, show it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
